import React, { useState, useEffect } from 'react';
import { Dna, Plus, Edit3, Trash2, Sparkles, Upload, ChevronDown, ChevronUp } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';

const sectionLabels = {
  summary: 'Summary',
  physical_signature: 'Physical Signature',
  metabolic_engine: 'Metabolic Engine',
  psychological_profile: 'Psychological Profile',
  health_vulnerabilities: 'Health & Vulnerabilities',
};

export default function AdminGenotypesPage() {
  const [genotypes, setGenotypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingGenotype, setEditingGenotype] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imageMode, setImageMode] = useState('prompt');
  const [uploadFile, setUploadFile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    image_prompt: '',
    motto: '',
    greatest_strength: '',
    greatest_challenge: '',
    sections: {
      summary: '',
      physical_signature: '',
      metabolic_engine: '',
      psychological_profile: '',
      health_vulnerabilities: '',
    },
  });

  useEffect(() => {
    loadGenotypes();
  }, []);

  const loadGenotypes = async () => {
    try {
      const response = await adminAPI.getGenotypes();
      setGenotypes(response.data.genotypes || []);
    } catch (error) {
      console.error('Failed to load genotypes:', error);
      toast.error('Failed to load genotypes');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      image_prompt: '',
      motto: '',
      greatest_strength: '',
      greatest_challenge: '',
      sections: {
        summary: '',
        physical_signature: '',
        metabolic_engine: '',
        psychological_profile: '',
        health_vulnerabilities: '',
      },
    });
    setImageMode('prompt');
    setUploadFile(null);
  };

  const handleAddGenotype = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Please enter a name');
      return;
    }

    setSaving(true);
    try {
      const response = await adminAPI.createGenotype({
        name: formData.name,
        image_prompt: formData.image_prompt || null,
        motto: formData.motto || null,
        greatest_strength: formData.greatest_strength || null,
        greatest_challenge: formData.greatest_challenge || null,
        sections: formData.sections,
      });
      const newGenotype = response.data;
      
      if (imageMode === 'upload' && uploadFile) {
        await adminAPI.uploadGenotypeImage(newGenotype.id, uploadFile);
      }
      
      toast.success(formData.image_prompt ? 'Genotype created with AI-generated image!' : 'Genotype created!');
      setShowAddDialog(false);
      resetForm();
      loadGenotypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create genotype');
    } finally {
      setSaving(false);
    }
  };

  const handleEditGenotype = (genotype) => {
    setEditingGenotype(genotype);
    setFormData({
      name: genotype.name || '',
      image_prompt: '',
      motto: genotype.motto || '',
      greatest_strength: genotype.greatest_strength || '',
      greatest_challenge: genotype.greatest_challenge || '',
      sections: {
        summary: genotype.sections?.summary || '',
        physical_signature: genotype.sections?.physical_signature || '',
        metabolic_engine: genotype.sections?.metabolic_engine || '',
        psychological_profile: genotype.sections?.psychological_profile || '',
        health_vulnerabilities: genotype.sections?.health_vulnerabilities || '',
      },
    });
  };

  const handleSaveGenotype = async () => {
    if (!editingGenotype) return;
    
    setSaving(true);
    try {
      await adminAPI.updateGenotype(editingGenotype.id, {
        name: formData.name,
        motto: formData.motto || null,
        greatest_strength: formData.greatest_strength || null,
        greatest_challenge: formData.greatest_challenge || null,
        sections: formData.sections,
      });
      
      if (uploadFile) {
        await adminAPI.uploadGenotypeImage(editingGenotype.id, uploadFile);
      }
      
      toast.success('Genotype updated');
      setEditingGenotype(null);
      resetForm();
      loadGenotypes();
    } catch (error) {
      toast.error('Failed to update genotype');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGenotype = async (genotypeId) => {
    try {
      await adminAPI.deleteGenotype(genotypeId);
      toast.success('Genotype deleted');
      loadGenotypes();
    } catch (error) {
      toast.error('Failed to delete genotype');
    }
  };

  const updateSection = (key, value) => {
    setFormData(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [key]: value,
      },
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  // Form JSX - inlined to avoid re-mount on state changes
  const renderGenotypeForm = (isEdit = false) => (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
      {/* Basic Info */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Name *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., Vaginal Type 1"
            required
            data-testid="genotype-name-input"
          />
        </div>

        {/* Highlights */}
        <div className="p-4 bg-[#E8A095]/10 rounded-xl space-y-3">
          <p className="text-sm font-medium text-[#4A2C2A]">Profile Highlights</p>
          <div className="space-y-2">
            <Label className="text-sm">Motto</Label>
            <Input
              value={formData.motto}
              onChange={(e) => setFormData(prev => ({ ...prev, motto: e.target.value }))}
              placeholder="A short tagline for this genotype..."
              data-testid="genotype-motto-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Greatest Strength</Label>
            <Input
              value={formData.greatest_strength}
              onChange={(e) => setFormData(prev => ({ ...prev, greatest_strength: e.target.value }))}
              placeholder="Their key advantage..."
              data-testid="genotype-strength-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Greatest Challenge</Label>
            <Input
              value={formData.greatest_challenge}
              onChange={(e) => setFormData(prev => ({ ...prev, greatest_challenge: e.target.value }))}
              placeholder="Their main challenge..."
              data-testid="genotype-challenge-input"
            />
          </div>
        </div>
      </div>

      {/* Image Options (only for create) */}
      {!isEdit && (
        <div className="space-y-3">
          <Label>Genotype Image</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={imageMode === 'prompt' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setImageMode('prompt')}
              className={imageMode === 'prompt' ? 'bg-[#E8A095] text-white' : ''}
            >
              <Sparkles className="w-4 h-4 mr-1" /> AI Generate
            </Button>
            <Button
              type="button"
              variant={imageMode === 'upload' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setImageMode('upload')}
              className={imageMode === 'upload' ? 'bg-[#E8A095] text-white' : ''}
            >
              <Upload className="w-4 h-4 mr-1" /> Upload
            </Button>
          </div>
          
          {imageMode === 'prompt' ? (
            <div className="space-y-2">
              <Textarea
                value={formData.image_prompt}
                onChange={(e) => setFormData(prev => ({ ...prev, image_prompt: e.target.value }))}
                placeholder="Describe the image you want AI to generate..."
                rows={2}
                data-testid="genotype-image-prompt"
              />
              <p className="text-xs text-[#4A2C2A]/50">Leave empty to skip image generation</p>
            </div>
          ) : (
            <div className="space-y-2">
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setUploadFile(e.target.files[0])}
                data-testid="genotype-image-upload"
              />
            </div>
          )}
        </div>
      )}

      {/* Update Image (for edit) */}
      {isEdit && (
        <div className="space-y-2">
          <Label>Update Image (optional)</Label>
          <Input
            type="file"
            accept="image/*"
            onChange={(e) => setUploadFile(e.target.files[0])}
          />
        </div>
      )}

      {/* Sections */}
      <div className="space-y-3">
        <Label>Profile Sections</Label>
        <Accordion type="multiple" className="w-full">
          {Object.entries(sectionLabels).map(([key, label]) => (
            <AccordionItem key={key} value={key} className="border-b border-[#E8A095]/10">
              <AccordionTrigger className="text-sm text-[#4A2C2A] hover:no-underline">
                {label}
                {formData.sections[key] && (
                  <span className="ml-2 text-xs text-[#8DA399]">✓</span>
                )}
              </AccordionTrigger>
              <AccordionContent>
                <Textarea
                  value={formData.sections[key]}
                  onChange={(e) => updateSection(key, e.target.value)}
                  placeholder={`Enter ${label.toLowerCase()} content...`}
                  rows={4}
                  className="mt-2"
                  data-testid={`genotype-section-${key}`}
                />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-genotypes-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl text-[#4A2C2A]">Genotypes</h1>
            <p className="text-[#4A2C2A]/60 mt-1">
              Manage user genotype profiles with detailed sections
            </p>
          </div>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#4A2C2A] text-white hover:bg-[#382120] rounded-xl" data-testid="add-genotype-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Genotype
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Create New Genotype</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddGenotype}>
                {renderGenotypeForm(false)}
                <Button
                  type="submit"
                  disabled={saving}
                  className="w-full mt-6 bg-[#4A2C2A] text-white hover:bg-[#382120]"
                  data-testid="save-genotype-btn"
                >
                  {saving ? (formData.image_prompt ? 'Creating & Generating Image...' : 'Creating...') : 'Create Genotype'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Genotypes Grid */}
        {genotypes.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {genotypes.map(genotype => (
              <div key={genotype.id} className="soft-card overflow-hidden">
                {/* Image */}
                <div className="h-40 bg-gradient-to-br from-[#8DA399]/20 to-[#E8A095]/20 flex items-center justify-center relative">
                  {genotype.image_url ? (
                    <img 
                      src={genotype.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${genotype.image_url}` : genotype.image_url}
                      alt={genotype.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Dna className="w-16 h-16 text-[#8DA399]/50" />
                  )}
                  {genotype.motto && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                      <p className="text-white text-sm italic">"{genotype.motto}"</p>
                    </div>
                  )}
                </div>
                
                {/* Content */}
                <div className="p-5">
                  <h3 className="font-heading text-lg text-[#4A2C2A] mb-2">{genotype.name}</h3>
                  
                  {/* Highlights */}
                  <div className="space-y-2 text-sm mb-4">
                    {genotype.greatest_strength && (
                      <div className="flex items-start gap-2">
                        <span className="text-[#8DA399]">+</span>
                        <span className="text-[#4A2C2A]/70">{genotype.greatest_strength}</span>
                      </div>
                    )}
                    {genotype.greatest_challenge && (
                      <div className="flex items-start gap-2">
                        <span className="text-[#E8A095]">!</span>
                        <span className="text-[#4A2C2A]/70">{genotype.greatest_challenge}</span>
                      </div>
                    )}
                  </div>

                  {/* Section indicators */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {Object.entries(sectionLabels).map(([key, label]) => (
                      <span 
                        key={key}
                        className={`px-2 py-0.5 rounded text-xs ${
                          genotype.sections?.[key] 
                            ? 'bg-[#8DA399]/20 text-[#8DA399]' 
                            : 'bg-[#4A2C2A]/10 text-[#4A2C2A]/40'
                        }`}
                      >
                        {label.split(' ')[0]}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#4A2C2A]/40">
                      {new Date(genotype.created_at).toLocaleDateString()}
                    </span>
                    <div className="flex items-center gap-1">
                      <Dialog open={editingGenotype?.id === genotype.id} onOpenChange={(open) => !open && setEditingGenotype(null)}>
                        <DialogTrigger asChild>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleEditGenotype(genotype)}
                            data-testid={`edit-genotype-${genotype.id}`}
                          >
                            <Edit3 className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle className="font-heading text-xl">Edit Genotype</DialogTitle>
                          </DialogHeader>
                          {renderGenotypeForm(true)}
                          <div className="flex justify-end gap-2 mt-4">
                            <Button variant="outline" onClick={() => setEditingGenotype(null)}>
                              Cancel
                            </Button>
                            <Button 
                              onClick={handleSaveGenotype} 
                              disabled={saving}
                              className="bg-[#4A2C2A] text-white hover:bg-[#382120]"
                            >
                              {saving ? 'Saving...' : 'Save Changes'}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                      
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-genotype-${genotype.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Genotype?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete "{genotype.name}" and remove it from any assigned users.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDeleteGenotype(genotype.id)}
                              className="bg-red-500 hover:bg-red-600"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="soft-card p-12 text-center">
            <Dna className="w-16 h-16 mx-auto mb-4 text-[#4A2C2A]/20" />
            <h2 className="font-heading text-xl text-[#4A2C2A] mb-2">No Genotypes Yet</h2>
            <p className="text-[#4A2C2A]/60 mb-6">
              Create genotype profiles with detailed sections to assign to users
            </p>
            <Button 
              onClick={() => setShowAddDialog(true)}
              className="bg-[#4A2C2A] text-white hover:bg-[#382120] rounded-xl"
            >
              <Plus className="w-4 h-4 mr-2" /> Create First Genotype
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
